import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DatePickerComponent } from '../date-picker/date-picker.component';
import {
  SlotCapacityPastOverlaySegment,
  SlotCapacityTrackComponent,
  SlotCapacityTrackSegment,
} from '../slot-capacity-track/slot-capacity-track.component';
import { SlotSelectionOverlayComponent, SlotSelectionOverlaySegment } from '../slot-selection-overlay/slot-selection-overlay.component';
import { SlotCalendarItem } from './slot-calendar.models';

export type { SlotCalendarItem } from './slot-calendar.models';

interface SlotCalendarHourRow {
  hourKey: string;
  hourLabel: string;
  slots: SlotCalendarItem[];
  capacitySegments: SlotCapacityTrackSegment[];
  pastSegments: SlotCapacityPastOverlaySegment[];
  selectionSegments: SlotSelectionOverlaySegment[];
}

@Component({
  selector: 'lib-slot-calendar',
  standalone: true,
  imports: [DatePickerComponent, SlotCapacityTrackComponent, SlotSelectionOverlayComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="slot-calendar" [class.slot-calendar--single-row-per-hour]="singleRowPerHour">
      @if (showDatePicker) {
        <div class="slot-calendar__date-picker">
          <lib-date-picker
            [label]="dateLabel"
            [value]="selectedDate"
            [min]="minDate"
            [disabled]="disabled"
            [inputId]="datePickerId"
            (valueChange)="onDateChanged($event)"
          />
        </div>
      }

      @if (showLegend) {
        <div class="slot-calendar__legend">
          @if (hasCapacityLegend) {
            <div class="slot-calendar__legend-scale">
              <span>{{ unavailableCapacityLabel }}</span>
              <span class="slot-calendar__legend-gradient"></span>
              <span>{{ availableCapacityLabel }}</span>
            </div>
            @if (showPastStatus) {
              <div class="slot-calendar__legend-item">
                <span class="slot-calendar__legend-swatch slot-calendar__legend-swatch--past-pattern"></span>
                <span>{{ pastOverlayLabel }}</span>
              </div>
            }
          } @else {
            <div class="slot-calendar__legend-item">
              <span class="slot-calendar__legend-swatch slot-calendar__legend-swatch--available"></span>
              <span>{{ availableLabel }}</span>
            </div>
            <div class="slot-calendar__legend-item">
              <span class="slot-calendar__legend-swatch slot-calendar__legend-swatch--reserved"></span>
              <span>{{ reservedLabel }}</span>
            </div>
            <div class="slot-calendar__legend-item">
              <span class="slot-calendar__legend-swatch slot-calendar__legend-swatch--blocked"></span>
              <span>{{ blockedLabel }}</span>
            </div>
            <div class="slot-calendar__legend-item">
              <span class="slot-calendar__legend-swatch slot-calendar__legend-swatch--completed"></span>
              <span>{{ completedLabel }}</span>
            </div>
            @if (showPastStatus) {
              <div class="slot-calendar__legend-item">
                <span class="slot-calendar__legend-swatch slot-calendar__legend-swatch--past"></span>
                <span>{{ pastLabel }}</span>
              </div>
            }
          }
        </div>
      }

      @if (slots.length === 0) {
        <div class="slot-calendar__empty">
          {{ emptySlotsText }}
        </div>
      } @else if (activeDateHourRows.length === 0) {
        <div class="slot-calendar__empty">
          {{ emptyDateText }}
        </div>
      } @else {
        @if (isRangeMode) {
          <div class="slot-calendar__day-groups">
            @for (group of rangeDayGroups; track group.dateKey) {
              <div class="slot-calendar__day-group">
                <div class="slot-calendar__day-title">{{ group.dateLabel }}</div>
                <div class="slot-calendar__slot-rows">
                  @for (row of group.rows; track row.hourKey) {
                    <div class="slot-calendar__slot-row">
                      <div class="slot-calendar__slot-row-hour">{{ row.hourLabel }}</div>
                      <div
                        class="slot-calendar__slot-row-items"
                        [class.slot-calendar__slot-row-items--capacity]="hasCapacityTrack(row)"
                      >
                        @if (hasCapacityTrack(row)) {
                          <lib-slot-capacity-track
                            [segments]="row.capacitySegments"
                            [pastSegments]="row.pastSegments"
                          />
                          <lib-slot-selection-overlay
                            [segments]="row.selectionSegments"
                            [disabled]="disabled"
                            (slotSelected)="slotSelected.emit($event)"
                          />
                        } @else {
                          @for (slot of row.slots; track slot.id) {
                            <button
                              type="button"
                              class="slot-calendar__slot-item"
                              [class.slot-calendar__slot-item--selected-start]="selectedSlotId === slot.id"
                              [class.slot-calendar__slot-item--selected-block]="isHighlighted(slot.id)"
                              [class.slot-calendar__slot-item--past]="isSlotInPast(slot)"
                              [class.slot-calendar__slot-item--available]="slot.status === 'available'"
                              [class.slot-calendar__slot-item--reserved]="slot.status === 'reserved'"
                              [class.slot-calendar__slot-item--blocked]="slot.status === 'blocked'"
                              [class.slot-calendar__slot-item--completed]="slot.status === 'completed'"
                              [class.slot-calendar__slot-item--aggregate]="isAggregateSlot(slot)"
                              [style.grid-column]="slotGridColumn(slot)"
                              [style.--slot-available-alpha]="slotAvailableAlpha(slot)"
                              [style.--slot-occupied-alpha]="slotOccupiedAlpha(slot)"
                              [disabled]="disabled || !isSlotSelectable(slot)"
                              [title]="slotTitle(slot)"
                            (click)="slotSelected.emit(slot.id)"
                          >
                              <span class="slot-calendar__slot-time">{{ formatTimeRange(slot) }}</span>
                              @if (slot.primaryLabel) {
                                <span class="slot-calendar__slot-primary">{{ slot.primaryLabel }}</span>
                              }
                              @if (slot.secondaryLabel) {
                                <span class="slot-calendar__slot-secondary">{{ slot.secondaryLabel }}</span>
                              }
                              @if (slot.metaLabel) {
                                <span class="slot-calendar__slot-meta">{{ slot.metaLabel }}</span>
                              }
                              <span class="slot-calendar__slot-status">{{ slotStatusLabel(slot) }}</span>
                            </button>
                          }
                        }
                      </div>
                    </div>
                  }
                </div>
              </div>
            }
          </div>
        } @else {
          <div class="slot-calendar__slot-rows">
            @for (row of activeDateHourRows; track row.hourKey) {
              <div class="slot-calendar__slot-row">
                <div class="slot-calendar__slot-row-hour">{{ row.hourLabel }}</div>
                <div
                  class="slot-calendar__slot-row-items"
                  [class.slot-calendar__slot-row-items--capacity]="hasCapacityTrack(row)"
                >
                  @if (hasCapacityTrack(row)) {
                    <lib-slot-capacity-track
                      [segments]="row.capacitySegments"
                      [pastSegments]="row.pastSegments"
                    />
                    <lib-slot-selection-overlay
                      [segments]="row.selectionSegments"
                      [disabled]="disabled"
                      (slotSelected)="slotSelected.emit($event)"
                    />
                  } @else {
                    @for (slot of row.slots; track slot.id) {
                      <button
                        type="button"
                        class="slot-calendar__slot-item"
                        [class.slot-calendar__slot-item--selected-start]="selectedSlotId === slot.id"
                        [class.slot-calendar__slot-item--selected-block]="isHighlighted(slot.id)"
                        [class.slot-calendar__slot-item--past]="isSlotInPast(slot)"
                        [class.slot-calendar__slot-item--available]="slot.status === 'available'"
                        [class.slot-calendar__slot-item--reserved]="slot.status === 'reserved'"
                        [class.slot-calendar__slot-item--blocked]="slot.status === 'blocked'"
                        [class.slot-calendar__slot-item--completed]="slot.status === 'completed'"
                        [class.slot-calendar__slot-item--aggregate]="isAggregateSlot(slot)"
                        [style.grid-column]="slotGridColumn(slot)"
                        [style.--slot-available-alpha]="slotAvailableAlpha(slot)"
                        [style.--slot-occupied-alpha]="slotOccupiedAlpha(slot)"
                        [disabled]="disabled || !isSlotSelectable(slot)"
                        [title]="slotTitle(slot)"
                        (click)="slotSelected.emit(slot.id)"
                      >
                        <span class="slot-calendar__slot-time">{{ formatTimeRange(slot) }}</span>
                        @if (slot.primaryLabel) {
                          <span class="slot-calendar__slot-primary">{{ slot.primaryLabel }}</span>
                        }
                        @if (slot.secondaryLabel) {
                          <span class="slot-calendar__slot-secondary">{{ slot.secondaryLabel }}</span>
                        }
                        @if (slot.metaLabel) {
                          <span class="slot-calendar__slot-meta">{{ slot.metaLabel }}</span>
                        }
                        <span class="slot-calendar__slot-status">{{ slotStatusLabel(slot) }}</span>
                      </button>
                    }
                  }
                </div>
              </div>
            }
          </div>
        }
      }
    </div>
  `,
  styles: [`
    .slot-calendar__date-picker {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 10px;
    }

    .slot-calendar__empty {
      font-size: 13px;
      color: #6b7280;
      padding: 6px 2px;
    }

    .slot-calendar__legend {
      margin-bottom: 12px;
      display: flex;
      flex-wrap: wrap;
      gap: 10px 16px;
      align-items: center;
      font-size: 12px;
      color: #4b5563;
    }

    .slot-calendar__legend-scale {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      min-width: min(100%, 300px);
    }

    .slot-calendar__legend-gradient {
      width: 128px;
      max-width: 36vw;
      height: 12px;
      border: 1px solid #cbd5e1;
      border-radius: 999px;
      background:
        linear-gradient(
          90deg,
          var(--tenant-slot-unavailable, #f3f4f6),
          var(--tenant-slot-pressure, #f59e0b),
          var(--tenant-slot-available, #22c55e)
        );
      flex: 0 1 128px;
    }

    .slot-calendar__legend-item {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }

    .slot-calendar__legend-swatch {
      width: 12px;
      height: 12px;
      border-radius: 999px;
      border: 1px solid #d1d5db;
      background: #ffffff;
      flex-shrink: 0;
    }

    .slot-calendar__legend-swatch--available {
      background: #ecfdf5;
      border-color: #86efac;
    }

    .slot-calendar__legend-swatch--reserved {
      background: #eff6ff;
      border-color: #93c5fd;
    }

    .slot-calendar__legend-swatch--blocked,
    .slot-calendar__legend-swatch--completed {
      background: #f3f4f6;
      border-color: #cbd5e1;
    }

    .slot-calendar__legend-swatch--past {
      background: #fee2e2;
      border-color: #fca5a5;
    }

    .slot-calendar__legend-swatch--past-pattern {
      border-color: #cbd5e1;
      background:
        repeating-linear-gradient(
          135deg,
          color-mix(
            in srgb,
            var(--tenant-slot-past-pattern, #64748b) var(--tenant-slot-past-pattern-opacity, 20%),
            transparent
          ) 0,
          color-mix(
            in srgb,
            var(--tenant-slot-past-pattern, #64748b) var(--tenant-slot-past-pattern-opacity, 20%),
            transparent
          ) 4px,
          rgba(255, 255, 255, 0) 4px,
          rgba(255, 255, 255, 0) 8px
        ),
        var(--tenant-slot-available, #22c55e);
    }

    .slot-calendar__slot-rows {
      display: grid;
      gap: 8px;
    }

    .slot-calendar__day-groups {
      display: grid;
      gap: 14px;
    }

    .slot-calendar__day-group {
      border-top: 1px solid #e5e7eb;
      padding-top: 10px;
    }

    .slot-calendar__day-group:first-child {
      border-top: none;
      padding-top: 0;
    }

    .slot-calendar__day-title {
      font-size: 12px;
      font-weight: 700;
      color: #1f2937;
      margin-bottom: 8px;
    }

    .slot-calendar__slot-row {
      display: grid;
      grid-template-columns: 72px 1fr;
      gap: 8px;
      align-items: start;
    }

    .slot-calendar__slot-row-hour {
      font-size: 12px;
      font-weight: 700;
      color: #374151;
      padding-top: 10px;
      text-align: right;
    }

    .slot-calendar__slot-row-items {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      grid-auto-rows: minmax(48px, auto);
      gap: 6px;
      position: relative;
    }

    .slot-calendar__slot-row-items--capacity {
      min-height: 48px;
      grid-template-columns: repeat(60, minmax(0, 1fr));
      grid-auto-rows: minmax(48px, auto);
      row-gap: 6px;
      column-gap: 0;
    }

    .slot-calendar--single-row-per-hour .slot-calendar__slot-row-items:not(.slot-calendar__slot-row-items--capacity) {
      grid-template-columns: none;
      grid-auto-flow: column;
      grid-auto-columns: minmax(120px, 1fr);
      min-width: 0;
      overflow-x: auto;
    }

    .slot-calendar__slot-item {
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 6px;
      display: grid;
      gap: 3px;
      text-align: left;
      cursor: pointer;
      background: #ffffff;
      min-width: 0;
      min-height: 48px;
    }

    .slot-calendar__slot-item:disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }

    .slot-calendar__slot-item--selected-block {
      border-color: #93c5fd;
      box-shadow: 0 0 0 1px #93c5fd inset;
    }

    .slot-calendar__slot-item--selected-start {
      border-color: #1d4ed8;
      box-shadow: 0 0 0 1px #1d4ed8 inset;
    }

    .slot-calendar__slot-item--available {
      background: #ecfdf5;
    }

    .slot-calendar__slot-item--aggregate {
      background:
        repeating-linear-gradient(
          135deg,
          rgba(100, 116, 139, var(--slot-occupied-alpha, 0)) 0,
          rgba(100, 116, 139, var(--slot-occupied-alpha, 0)) 6px,
          rgba(255, 255, 255, 0) 6px,
          rgba(255, 255, 255, 0) 12px
        ),
        linear-gradient(
          0deg,
          rgba(34, 197, 94, var(--slot-available-alpha, 0.12)),
          rgba(34, 197, 94, var(--slot-available-alpha, 0.12))
        ),
        #ffffff;
      border-color: #bbf7d0;
    }

    .slot-calendar__slot-item--reserved {
      background:
        repeating-linear-gradient(
          135deg,
          rgba(148, 163, 184, 0.20) 0,
          rgba(148, 163, 184, 0.20) 6px,
          rgba(248, 250, 252, 0.92) 6px,
          rgba(248, 250, 252, 0.92) 12px
        );
      border-color: #cbd5e1;
    }

    .slot-calendar__slot-item--blocked,
    .slot-calendar__slot-item--completed {
      background:
        repeating-linear-gradient(
          135deg,
          rgba(100, 116, 139, 0.20) 0,
          rgba(100, 116, 139, 0.20) 6px,
          rgba(241, 245, 249, 0.95) 6px,
          rgba(241, 245, 249, 0.95) 12px
        );
      border-color: #cbd5e1;
      color: #475569;
    }

    .slot-calendar__slot-item--past {
      background: #fee2e2;
      border-color: #fca5a5;
    }

    .slot-calendar__slot-item--past.slot-calendar__slot-item--reserved,
    .slot-calendar__slot-item--past.slot-calendar__slot-item--blocked,
    .slot-calendar__slot-item--past.slot-calendar__slot-item--completed {
      background:
        repeating-linear-gradient(
          135deg,
          rgba(100, 116, 139, 0.24) 0,
          rgba(100, 116, 139, 0.24) 6px,
          rgba(241, 245, 249, 0.95) 6px,
          rgba(241, 245, 249, 0.95) 12px
        );
      border-color: #cbd5e1;
      color: #475569;
    }

    .slot-calendar__slot-time {
      font-size: 11px;
      font-weight: 600;
      color: #111827;
    }

    .slot-calendar__slot-primary {
      font-size: 11px;
      line-height: 1.25;
      color: #111827;
      font-weight: 600;
      overflow-wrap: anywhere;
    }

    .slot-calendar__slot-secondary {
      font-size: 11px;
      line-height: 1.25;
      color: #475569;
      overflow-wrap: anywhere;
    }

    .slot-calendar__slot-meta {
      font-size: 11px;
      line-height: 1.25;
      color: #6b7280;
      overflow-wrap: anywhere;
    }

    .slot-calendar__slot-status {
      font-size: 11px;
      color: #6b7280;
      text-transform: capitalize;
    }

    @media (max-width: 900px) {
      .slot-calendar__slot-row {
        grid-template-columns: 48px 1fr;
      }

      .slot-calendar__slot-row-hour {
        text-align: left;
        padding-top: 4px;
      }
    }

    @media (max-width: 640px) {
      .slot-calendar__slot-row {
        grid-template-columns: minmax(0, 1fr);
        gap: 6px;
      }

      .slot-calendar__slot-row-hour {
        padding-top: 0;
      }

      .slot-calendar__slot-row-items {
        grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
        gap: 4px;
      }

      .slot-calendar__slot-row-items--capacity {
        grid-template-columns: repeat(60, minmax(0, 1fr));
        row-gap: 4px;
        column-gap: 0;
      }

      .slot-calendar__slot-item {
        border-radius: 10px;
        padding: 5px;
        gap: 2px;
      }

      .slot-calendar__slot-time {
        font-size: 10px;
      }

      .slot-calendar__slot-primary {
        font-size: 10px;
        line-height: 1.2;
      }

      .slot-calendar__slot-secondary,
      .slot-calendar__slot-meta {
        display: none;
      }

      .slot-calendar__slot-status {
        font-size: 10px;
      }
    }
  `],
})
export class SlotCalendarComponent implements OnChanges {
  @Input() public slots: SlotCalendarItem[] = [];
  @Input() public singleRowPerHour = false;
  @Input() public hideUnavailableHours = false;
  @Input() public timeZone: string | null = null;
  @Input() public selectedDate = '';
  @Input() public minDate: string | null = null;
  @Input() public rangeStartDate: string | null = null;
  @Input() public rangeEndDate: string | null = null;
  @Input() public selectedSlotId: string | null = null;
  @Input() public highlightedSlotIds: string[] = [];
  @Input() public showDatePicker = true;
  @Input() public dateLabel = 'Date';
  @Input() public datePickerId = `slot-calendar-date-${Math.random().toString(36).slice(2, 10)}`;
  @Input() public emptySlotsText = 'No available slots for selected filters.';
  @Input() public emptyDateText = 'No slots available for selected date.';
  @Input() public selectableStatuses: string[] = ['available'];
  @Input() public allowPastSelection = false;
  @Input() public disabled = false;
  @Input() public showPastStatus = true;
  @Input() public showLegend = true;
  @Input() public legendMode: 'auto' | 'status' | 'capacity' = 'auto';
  @Input() public availableLabel = 'Available';
  @Input() public reservedLabel = 'Reserved';
  @Input() public blockedLabel = 'Blocked';
  @Input() public completedLabel = 'Completed';
  @Input() public pastLabel = 'Past';
  @Input() public availableCapacityLabel = 'Available';
  @Input() public unavailableCapacityLabel = 'Unavailable';
  @Input() public pastOverlayLabel = 'Past overlay';

  @Output() public readonly selectedDateChange = new EventEmitter<string>();
  @Output() public readonly slotSelected = new EventEmitter<string>();

  private activeDateSlotsCache: SlotCalendarItem[] = [];
  private activeDateHourRowsCache: SlotCalendarHourRow[] = [];
  private rangeDayGroupsCache: Array<{
    dateKey: string;
    dateLabel: string;
    rows: SlotCalendarHourRow[];
  }> = [];
  private hasCapacityLegendCache = false;
  private calendarNowMs = Date.now();
  private readonly formatterCache = new Map<string, Intl.DateTimeFormat>();

  public ngOnChanges(_changes: SimpleChanges): void {
    this.rebuildCalendarState();
  }

  protected get isRangeMode(): boolean {
    const start = this.rangeStartDate?.trim() ?? '';
    const end = this.rangeEndDate?.trim() ?? '';
    return !!start && !!end && start !== end;
  }

  protected get activeDateSlots(): SlotCalendarItem[] {
    return this.activeDateSlotsCache;
  }

  protected get activeDateHourRows(): SlotCalendarHourRow[] {
    return this.activeDateHourRowsCache;
  }

  protected get rangeDayGroups(): Array<{
    dateKey: string;
    dateLabel: string;
    rows: SlotCalendarHourRow[];
  }> {
    return this.rangeDayGroupsCache;
  }

  private rebuildCalendarState(): void {
    this.calendarNowMs = Date.now();
    this.activeDateSlotsCache = this.resolveActiveDateSlots();
    this.activeDateHourRowsCache = this.buildRows(this.activeDateSlotsCache, this.calendarNowMs);
    this.rangeDayGroupsCache = this.resolveRangeDayGroups(this.calendarNowMs);
    const rows = this.isRangeMode
      ? this.rangeDayGroupsCache.flatMap((group) => group.rows)
      : this.activeDateHourRowsCache;
    this.hasCapacityLegendCache = rows.some((row) => this.hasCapacityTrack(row));
  }

  private resolveActiveDateSlots(): SlotCalendarItem[] {
    if (this.isRangeMode) {
      const start = this.rangeStartDate?.trim() ?? '';
      const end = this.rangeEndDate?.trim() ?? '';
      if (!start || !end) {
        return [];
      }

      return this.slots.filter((slot) => {
        const key = this.dateKey(slot.startTime, slot);
        return key >= start && key <= end;
      });
    }

    if (!this.selectedDate) {
      return [];
    }

    return this.slots.filter((slot) => this.dateKey(slot.startTime, slot) === this.selectedDate);
  }

  private resolveRangeDayGroups(nowMs: number): Array<{
    dateKey: string;
    dateLabel: string;
    rows: SlotCalendarHourRow[];
  }> {
    if (!this.isRangeMode) {
      return [];
    }

    const byDay = new Map<string, SlotCalendarItem[]>();
    for (const slot of this.activeDateSlots) {
      const key = this.dateKey(slot.startTime, slot);
      const list = byDay.get(key) ?? [];
      list.push(slot);
      byDay.set(key, list);
    }

    return Array.from(byDay.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([dateKey, slots]) => ({
        dateKey,
        dateLabel: this.formatDateLabel(dateKey),
        rows: this.buildRows(slots, nowMs),
      }));
  }

  protected onDateChanged(value: string): void {
    const minDate = this.minDate?.trim() ?? '';
    const nextDate = (value?.trim() ?? '') || this.todayDateKey();
    this.selectedDateChange.emit(minDate && nextDate < minDate ? minDate : nextDate);
  }

  protected isHighlighted(slotId: string): boolean {
    return this.highlightedSlotIds.includes(slotId);
  }

  protected isSlotSelectable(slot: SlotCalendarItem): boolean {
    if (!this.allowPastSelection && this.isSlotInPast(slot)) {
      return false;
    }

    return this.selectableStatuses.includes(slot.status);
  }

  protected slotStatusLabel(slot: SlotCalendarItem): string {
    if (this.showPastStatus && this.isSlotInPastAt(slot, this.calendarNowMs)) {
      return 'past';
    }

    return slot.status;
  }

  protected slotTitle(slot: SlotCalendarItem, nowMs = this.calendarNowMs): string {
    const parts = [
      `${this.formatTime(slot.startTime, slot)} -> ${this.formatTime(slot.endTime, slot)}`,
      slot.primaryLabel?.trim() || '',
      slot.secondaryLabel?.trim() || '',
      slot.metaLabel?.trim() || '',
      `(${this.slotStatusLabelAt(slot, nowMs)})`,
    ].filter(Boolean);

    return parts.join(' • ');
  }

  private slotStatusLabelAt(slot: SlotCalendarItem, nowMs: number): string {
    if (this.showPastStatus && this.isSlotInPastAt(slot, nowMs)) {
      return 'past';
    }

    return slot.status;
  }

  protected isAggregateSlot(slot: SlotCalendarItem): boolean {
    return typeof slot.totalCount === 'number' && slot.totalCount > 0;
  }

  protected hasCapacityTrack(row: SlotCalendarHourRow): boolean {
    return row.capacitySegments.length > 0;
  }

  protected get hasCapacityLegend(): boolean {
    if (this.legendMode === 'capacity') {
      return true;
    }
    if (this.legendMode === 'status') {
      return false;
    }

    return this.hasCapacityLegendCache;
  }

  protected slotAvailableAlpha(slot: SlotCalendarItem): string {
    const total = slot.totalCount ?? 0;
    const available = slot.availableCount ?? 0;
    if (total <= 0) {
      return '0.12';
    }

    const ratio = Math.max(0, Math.min(1, available / total));
    return String(0.08 + ratio * 0.24);
  }

  protected slotOccupiedAlpha(slot: SlotCalendarItem): string {
    const total = slot.totalCount ?? 0;
    const occupied = slot.occupiedCount ?? 0;
    if (total <= 0 || occupied <= 0) {
      return '0';
    }

    const ratio = Math.max(0, Math.min(1, occupied / total));
    return String(0.10 + ratio * 0.24);
  }

  protected formatMinute(value: string): string {
    const parts = this.extractDateTimeParts(value);
    if (parts) {
      return parts.minutes;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '--';
    }

    return String(date.getMinutes()).padStart(2, '0');
  }

  protected slotGridColumn(slot: SlotCalendarItem): string {
    if (!this.isAggregateSlot(slot)) {
      return 'auto';
    }

    const startMinute = this.minuteOfHour(slot.startTime, slot);
    const endMinute = this.minuteOfHour(slot.endTime, slot);
    const start = Math.max(0, Math.min(59, startMinute));
    const rawSpan = endMinute > startMinute
      ? endMinute - startMinute
      : 60 - startMinute;
    const span = Math.max(1, Math.min(60 - start, rawSpan));

    return `${start + 1} / span ${span}`;
  }

  protected formatTimeRange(slot: SlotCalendarItem): string {
    return `${this.formatTime(slot.startTime, slot)} -> ${this.formatTime(slot.endTime, slot)}`;
  }

  private formatTime(value: string, slot?: SlotCalendarItem): string {
    const parts = this.extractDateTimeParts(value);
    if (parts && !this.hasExplicitTimeZone(value)) {
      return `${parts.hours}:${parts.minutes}`;
    }

    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return this.cachedFormatter('time', this.resolveTimeZone(slot), {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(date);
    }

    if (parts) {
      return `${parts.hours}:${parts.minutes}`;
    }

    return value;
  }

  private dateKey(value: string, slot?: SlotCalendarItem): string {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return '';
    }

    const parts = this.extractDateTimeParts(normalized);
    if (parts && !this.hasExplicitTimeZone(normalized)) {
      return `${parts.year}-${parts.month}-${parts.day}`;
    }

    const date = new Date(normalized);
    if (!Number.isNaN(date.getTime())) {
      return this.cachedFormatter('date-key', this.resolveTimeZone(slot), {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(date);
    }

    if (parts) {
      return `${parts.year}-${parts.month}-${parts.day}`;
    }

    return normalized.slice(0, 10);
  }

  private buildRows(slots: SlotCalendarItem[], nowMs: number): SlotCalendarHourRow[] {
    const startGroups = new Map<string, SlotCalendarItem[]>();
    const capacityGroups = new Map<string, SlotCalendarItem[]>();

    for (const slot of slots) {
      const key = this.hourKey(slot.startTime, slot);
      const list = startGroups.get(key) ?? [];
      list.push(slot);
      startGroups.set(key, list);

      if (this.isAggregateSlot(slot)) {
        for (const capacityKey of this.capacityHourKeys(slot)) {
          const capacityList = capacityGroups.get(capacityKey) ?? [];
          capacityList.push(slot);
          capacityGroups.set(capacityKey, capacityList);
        }
      }
    }

    const hourKeys = new Set([...startGroups.keys(), ...capacityGroups.keys()]);

    return Array.from(hourKeys)
      .sort((a, b) => a.localeCompare(b))
      .filter((hourKey) => !this.hideUnavailableHours ||
        (startGroups.get(hourKey) ?? []).some((slot) => this.isSlotSelectable(slot)),
      )
      .map((hourKey) => {
        const rowSlots = startGroups.get(hourKey) ?? [];
        const capacitySlots = capacityGroups.get(hourKey) ?? [];
        return {
          hourKey,
          hourLabel: `${hourKey}:`,
          slots: rowSlots.sort((a, b) => a.startTime.localeCompare(b.startTime)),
          capacitySegments: this.buildCapacityTrackSegments(hourKey, capacitySlots, nowMs),
          pastSegments: this.buildPastOverlaySegments(hourKey, capacitySlots, nowMs),
          selectionSegments: this.buildSelectionOverlaySegments(hourKey, capacitySlots, nowMs),
        };
      });
  }

  private buildPastOverlaySegments(
    hourKey: string,
    slots: SlotCalendarItem[],
    now: number,
  ): SlotCapacityPastOverlaySegment[] {
    const ranges = slots
      .map((slot) => this.pastOverlayRange(hourKey, slot, now))
      .filter((range): range is {
        startPercent: number;
        endPercent: number;
        fadeStartPercent: number;
        fadesToNow: boolean;
      } => range !== null)
      .sort((a, b) => a.startPercent - b.startPercent);

    const merged: Array<{
      startPercent: number;
      endPercent: number;
      fadeStartPercent: number;
      fadesToNow: boolean;
    }> = [];
    for (const range of ranges) {
      const previous = merged[merged.length - 1];
      if (previous && range.startPercent <= previous.endPercent + 0.01) {
        previous.endPercent = Math.max(previous.endPercent, range.endPercent);
        if (range.fadesToNow) {
          previous.fadeStartPercent = range.fadeStartPercent;
          previous.fadesToNow = true;
        } else if (!previous.fadesToNow) {
          previous.fadeStartPercent = previous.endPercent;
        }
      } else {
        merged.push({ ...range });
      }
    }

    return merged.map((range, index) => ({
      id: `${hourKey}:past:${index}`,
      startPercent: range.startPercent,
      widthPercent: range.endPercent - range.startPercent,
      fadeStartPercent: range.fadeStartPercent,
    }));
  }

  private pastOverlayRange(
    hourKey: string,
    slot: SlotCalendarItem,
    now: number,
  ): { startPercent: number; endPercent: number; fadeStartPercent: number; fadesToNow: boolean } | null {
    const slotStart = this.parseSlotDateToMs(slot.startTime);
    const slotEnd = this.parseSlotDateToMs(slot.endTime);
    if (Number.isNaN(slotStart) || Number.isNaN(slotEnd) || slotStart >= now) {
      return null;
    }

    const bounds = this.hourSegmentBounds(hourKey, slot);
    if (!bounds) {
      return null;
    }

    const endPercent = bounds.startPercent + bounds.widthPercent;
    if (slotEnd <= now) {
      return {
        startPercent: bounds.startPercent,
        endPercent,
        fadeStartPercent: endPercent,
        fadesToNow: false,
      };
    }

    const nowHourKey = this.hourKeyFromDate(new Date(now), slot);
    if (nowHourKey !== hourKey) {
      return {
        startPercent: bounds.startPercent,
        endPercent,
        fadeStartPercent: endPercent,
        fadesToNow: false,
      };
    }

    const nowPercent = (this.minuteOfDate(new Date(now), slot) / 60) * 100;
    const clampedNowPercent = Math.max(bounds.startPercent, Math.min(endPercent, nowPercent));
    if (clampedNowPercent <= bounds.startPercent) {
      return null;
    }

    return {
      startPercent: bounds.startPercent,
      endPercent: clampedNowPercent,
      fadeStartPercent: bounds.startPercent,
      fadesToNow: true,
    };
  }

  private buildSelectionOverlaySegments(
    hourKey: string,
    slots: SlotCalendarItem[],
    nowMs: number,
  ): SlotSelectionOverlaySegment[] {
    return slots
      .map((slot) => this.buildSelectionOverlaySegment(hourKey, slot, nowMs))
      .filter((segment): segment is SlotSelectionOverlaySegment => segment !== null)
      .sort((a, b) => a.startPercent - b.startPercent);
  }

  private buildSelectionOverlaySegment(
    hourKey: string,
    slot: SlotCalendarItem,
    nowMs: number,
  ): SlotSelectionOverlaySegment | null {
    const bounds = this.hourSegmentBounds(hourKey, slot);
    if (!bounds) {
      return null;
    }

    return {
      id: slot.id,
      startPercent: bounds.startPercent,
      widthPercent: bounds.widthPercent,
      timeLabel: this.formatTimeRange(slot),
      primaryLabel: slot.primaryLabel,
      status: slot.status,
      past: this.isSlotInPastAt(slot, nowMs),
      selected: this.selectedSlotId === slot.id,
      highlighted: this.isHighlighted(slot.id),
      selectable: this.isSlotSelectableAt(slot, nowMs),
      title: this.slotTitle(slot, nowMs),
    };
  }

  private buildCapacityTrackSegments(
    hourKey: string,
    slots: SlotCalendarItem[],
    nowMs: number,
  ): SlotCapacityTrackSegment[] {
    return slots
      .map((slot) => this.buildCapacityTrackSegment(hourKey, slot, nowMs))
      .filter((segment): segment is SlotCapacityTrackSegment => segment !== null)
      .sort((a, b) => a.startPercent - b.startPercent);
  }

  private buildCapacityTrackSegment(
    hourKey: string,
    slot: SlotCalendarItem,
    nowMs: number,
  ): SlotCapacityTrackSegment | null {
    const bounds = this.hourSegmentBounds(hourKey, slot);
    if (!bounds) {
      return null;
    }

    const totalCount = Math.max(0, slot.totalCount ?? 0);
    const availableCount = Math.max(0, Math.min(totalCount, slot.availableCount ?? 0));
    const occupiedCount = Math.max(0, Math.min(totalCount, slot.occupiedCount ?? 0));

    return {
      id: `${slot.id}:${hourKey}`,
      startPercent: bounds.startPercent,
      widthPercent: bounds.widthPercent,
      availableCount,
      occupiedCount,
      totalCount,
      status: slot.status,
      past: this.isSlotInPastAt(slot, nowMs),
      hardUnavailable: !!slot.hardUnavailable,
      title: this.slotTitle(slot, nowMs),
    };
  }

  private hourSegmentBounds(
    hourKey: string,
    slot: SlotCalendarItem,
  ): { startPercent: number; widthPercent: number } | null {
    const startHour = this.hourKey(slot.startTime, slot);
    const endHour = this.hourKey(slot.endTime, slot);
    const startMinute = startHour === hourKey ? this.minuteOfHour(slot.startTime, slot) : 0;
    const rawEndMinute = endHour === hourKey ? this.minuteOfHour(slot.endTime, slot) : 60;
    const endMinute = rawEndMinute === 0 && startHour !== endHour ? 60 : rawEndMinute;
    const clampedStart = Math.max(0, Math.min(60, startMinute));
    const clampedEnd = Math.max(0, Math.min(60, endMinute));
    if (clampedEnd <= clampedStart) {
      return null;
    }

    return {
      startPercent: (clampedStart / 60) * 100,
      widthPercent: ((clampedEnd - clampedStart) / 60) * 100,
    };
  }

  private capacityHourKeys(slot: SlotCalendarItem): string[] {
    const startHour = Number(this.hourKey(slot.startTime, slot));
    const endHour = Number(this.hourKey(slot.endTime, slot));
    const endMinute = this.minuteOfHour(slot.endTime, slot);
    if (!Number.isFinite(startHour) || !Number.isFinite(endHour)) {
      return [this.hourKey(slot.startTime, slot)];
    }

    let normalizedEndHour = endHour;
    if (normalizedEndHour < startHour) {
      normalizedEndHour += 24;
    }
    if (endMinute === 0 && normalizedEndHour > startHour) {
      normalizedEndHour -= 1;
    }

    const keys: string[] = [];
    for (let hour = startHour; hour <= normalizedEndHour; hour += 1) {
      keys.push(String(hour % 24).padStart(2, '0'));
    }

    return keys.length > 0 ? keys : [String(startHour).padStart(2, '0')];
  }

  private formatDateLabel(value: string): string {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) {
      return value;
    }

    const [, year, month, day] = match;
    return `${day}.${month}.${year}`;
  }

  private hourKey(value: string, slot?: SlotCalendarItem): string {
    const parts = this.extractDateTimeParts(value);
    if (parts && !this.hasExplicitTimeZone(value)) {
      return parts.hours;
    }

    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return this.cachedFormatter('hour', this.resolveTimeZone(slot), {
        hour: '2-digit',
        hour12: false,
      }).format(date);
    }

    if (parts) {
      return parts.hours;
    }

    return '00';
  }

  private minuteOfHour(value: string, slot?: SlotCalendarItem): number {
    const parts = this.extractDateTimeParts(value);
    if (parts && !this.hasExplicitTimeZone(value)) {
      return Number(parts.minutes);
    }

    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      const formattedParts = this.cachedFormatter('minute', this.resolveTimeZone(slot), {
        minute: '2-digit',
      }).formatToParts(date);
      const minute = formattedParts.find((part) => part.type === 'minute')?.value ?? '0';
      return Number(minute);
    }

    if (parts) {
      return Number(parts.minutes);
    }

    return 0;
  }

  private hourKeyFromDate(date: Date, slot?: SlotCalendarItem): string {
    return this.cachedFormatter('hour', this.resolveTimeZone(slot), {
      hour: '2-digit',
      hour12: false,
    }).format(date);
  }

  private minuteOfDate(date: Date, slot?: SlotCalendarItem): number {
    const formattedParts = this.cachedFormatter('minute', this.resolveTimeZone(slot), {
      minute: '2-digit',
    }).formatToParts(date);
    const minute = formattedParts.find((part) => part.type === 'minute')?.value ?? '0';
    return Number(minute);
  }

  private extractDateTimeParts(
    value: string,
  ): { year: string; month: string; day: string; hours: string; minutes: string } | null {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return null;
    }

    const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (!match) {
      return null;
    }

    const [, year, month, day, hours, minutes] = match;
    return { year, month, day, hours, minutes };
  }

  private hasExplicitTimeZone(value: string): boolean {
    return /[zZ]|[+-]\d{2}:\d{2}$/.test(value?.trim() ?? '');
  }

  private resolveTimeZone(slot?: SlotCalendarItem): string {
    const itemTimeZone = slot?.timeZone?.trim();
    return itemTimeZone || this.normalizedTimeZone;
  }

  protected isSlotInPast(slot: SlotCalendarItem): boolean {
    return this.isSlotInPastAt(slot, this.calendarNowMs);
  }

  private isSlotSelectableAt(slot: SlotCalendarItem, nowMs: number): boolean {
    if (!this.allowPastSelection && this.isSlotInPastAt(slot, nowMs)) {
      return false;
    }

    return this.selectableStatuses.includes(slot.status);
  }

  private isSlotInPastAt(slot: SlotCalendarItem, nowMs: number): boolean {
    const slotStart = this.parseSlotDateToMs(slot.startTime);
    if (Number.isNaN(slotStart)) {
      return false;
    }

    return slotStart < nowMs;
  }

  private parseSlotDateToMs(value: string): number {
    const parsed = new Date(value).getTime();
    if (!Number.isNaN(parsed)) {
      return parsed;
    }

    const parts = this.extractDateTimeParts(value);
    if (!parts) {
      return Number.NaN;
    }

    const year = Number(parts.year);
    const month = Number(parts.month);
    const day = Number(parts.day);
    const hours = Number(parts.hours);
    const minutes = Number(parts.minutes);
    return new Date(year, month - 1, day, hours, minutes, 0, 0).getTime();
  }

  private todayDateKey(): string {
    return this.cachedFormatter('today-date-key', this.normalizedTimeZone, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  }

  private cachedFormatter(
    name: string,
    timeZone: string,
    options: Intl.DateTimeFormatOptions,
  ): Intl.DateTimeFormat {
    const key = `${name}|${timeZone}`;
    const cached = this.formatterCache.get(key);
    if (cached) {
      return cached;
    }

    const locale = name === 'date-key' || name === 'today-date-key' ? 'en-CA' : 'en-GB';
    const formatter = new Intl.DateTimeFormat(locale, {
      ...options,
      timeZone,
    });
    this.formatterCache.set(key, formatter);
    return formatter;
  }

  private get normalizedTimeZone(): string {
    const candidate = this.timeZone?.trim();
    return candidate || 'UTC';
  }
}
